import React, {useEffect, useState, useCallback, useMemo} from "react";
import { ThemeColors } from "../../../app/slices/profile";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {Alert, Button, Col, Form, InputGroup, Modal} from "react-bootstrap";
import {v4 as uuidv4} from 'uuid';
import {
    changeModal,
    closeModal,
    deleteTheme,
    getTheme,
    saveNewTheme,
    saveTheme
} from "../../../app/slices/modals/themeModal";
import {ADMIN_LANG} from "../../../settings";
import AdminLoader from "../AdminLoader";
import Confirm from "../Confirm";
import {useNavigate} from "react-router-dom";
import {ExclamationTriangleFill} from "react-bootstrap-icons";
import {isFulfilled} from "@reduxjs/toolkit";
import ColorPicker from "./themeModal/ColorPicker";
import MediaPreview from "../MediaPreview";

const ThemeModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isOpen,
        loading,
        name,
        theme_id,
        background_image,
        logo,
        colors,
        used_in,
        error,
        game_id,
        section_id,
        delete_message
    } = useAppSelector(state => state.themeModal);

    const defaultColors = useAppSelector(state => state.profile.defaultColors);
    const [backgroundImage, setBackgroundImage] = useState<File | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const handleDelete = () => {
        setShowConfirmDelete(true)
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (theme_id) {
            dispatch(deleteTheme(theme_id));
        }
    };

    // This modal is mounted for the whole life of the admin app, so local state outlives every
    // open/close. Without this reset a File picked for one theme stayed queued and was attached to
    // the next theme saved - and a theme that already has an image renders a preview instead of a
    // file input, so there was nothing on screen to reveal it. Must run before the fetch below.
    useEffect(() => {
        if (isOpen) {
            setBackgroundImage(null);
            setLogoFile(null);
            setShowConfirmDelete(false);
        }
    }, [isOpen, theme_id]);

    useEffect(() => {
        if (theme_id) {
            dispatch(getTheme(theme_id));
        }
    }, [theme_id, dispatch]);

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', name);

        if (backgroundImage) {
            formData.append('image', backgroundImage, uuidv4() + backgroundImage.name);
        }
        if (logoFile) {
            formData.append('logo', logoFile, uuidv4() + logoFile.name);
        }

        if (theme_id) {
            formData.append('theme_id', theme_id.toString());
        }
        if (game_id) {
            formData.append('game_id', game_id);
        }
        if (section_id) {
            formData.append('section_id', section_id);
        }
        if (colors) {
            formData.append('colors', JSON.stringify(colors));
        }

        const saveAction = theme_id ? saveTheme : saveNewTheme;

        dispatch(saveAction(formData)).then((resultAction) => {
            let newId = '0'
            if (resultAction.payload && resultAction.payload.theme_id) {
                newId = resultAction.payload.theme_id
            }
            if (!theme_id && !game_id && !section_id && isFulfilled(resultAction)) {
                navigate(`/admin/themes/?new=${newId}`);
            }
        });
    };

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeModal({name: e.target.value}));
    }, [dispatch]);

    const handleBackgroundChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setBackgroundImage(e.target.files[0]);
        }
    }, []);

    const handleLogoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setLogoFile(e.target.files[0]);
        }
    }, []);

    const handleChangeImage = useCallback(() => {
        dispatch(changeModal({background_image: null}));
    }, [dispatch]);

    const handleChangeLogo = useCallback(() => {
        dispatch(changeModal({logo: null}));
    }, [dispatch]);

    const handleColorChange = useCallback((colorKey: keyof ThemeColors) => (value: string) => {
        dispatch(changeModal({
            colors: {
                ...colors,
                [colorKey]: value
            }
        }));
    }, [dispatch, colors]);

    const themeTitle = useMemo(() => (theme_id ? name : ADMIN_LANG.new_theme), [theme_id, name]);
    const showSave = !theme_id || !(game_id || section_id);

    return (
        <Modal backdropClassName={(game_id || section_id) ? 'nested-backdrop' : ''} size='xl' show={isOpen}
               onHide={() => dispatch(closeModal())} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{themeTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body className='container-fluid'>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                {used_in && (
                    <p className='text-info fw-semibold'>
                        <ExclamationTriangleFill className="me-2"/>
                        {ADMIN_LANG.theme_info}<br></br>
                    </p>
                )}
                <Form id='theme-modal-form' className='row gy-4' onSubmit={handleSave}>
                    <Col xs={12}>
                        <Form.Group controlId='name'>
                            <Form.Label>{ADMIN_LANG.name}</Form.Label>
                            <Form.Control type='text' required value={name} onChange={handleNameChange}
                                        maxLength={255}/>
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        {!background_image ? (
                            <Form.Group controlId='image'>
                                <Form.Label>{ADMIN_LANG.background_image}</Form.Label>
                                <InputGroup>
                                    <Form.Control required type='file' onChange={handleBackgroundChange}
                                                accept='image/jpeg,image/png,image/jpg'/>
                                </InputGroup>
                                <Form.Text className='help-text'
                                         dangerouslySetInnerHTML={{__html: ADMIN_LANG.theme_image_help}}></Form.Text>
                            </Form.Group>
                        ) : (
                            <Form.Group>
                                <Form.Label>{ADMIN_LANG.background_image}</Form.Label>
                                {/* Deliberately the full image, not background_image.thumbnail_url:
                                    a theme's thumbnail is a 100x100 centre crop sized for the 70x70
                                    theme-list cell, which would be both tiny and cropped here. */}
                                <MediaPreview src={background_image.url} alt={name}
                                              onAction={handleChangeImage}
                                              actionLabel={ADMIN_LANG.change}/>
                            </Form.Group>
                        )}
                    </Col>
                    <Col xs={12}>
                        {!logo ? (
                            <Form.Group controlId='image'>
                                <Form.Label>{ADMIN_LANG.logo}</Form.Label>
                                <InputGroup>
                                    <Form.Control type='file' onChange={handleLogoChange}
                                                accept='image/jpeg,image/png,image/jpg,image/svg+xml'/>
                                </InputGroup>
                                <Form.Text className='help-text'
                                         dangerouslySetInnerHTML={{__html: ADMIN_LANG.theme_logo_help}}></Form.Text>
                            </Form.Group>
                        ) : (
                            <Form.Group>
                                <Form.Label>{ADMIN_LANG.logo}</Form.Label>
                                <MediaPreview src={logo.url} alt={name}
                                              onAction={handleChangeLogo}
                                              actionLabel={ADMIN_LANG.change}
                                              backdropColor={colors?.top_bar_background || 'transparent'}/>
                            </Form.Group>
                        )}
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="top_bar_background"
                            label="Yläpalkin taustaväri"
                            value={colors?.top_bar_background || ''}
                            onChange={handleColorChange('top_bar_background')}
                            defaultValue={defaultColors.top_bar_background}
                            required={true}
                        />
                    </Col>
                    <hr style={{margin: '0', border: 'none'}}></hr>

                    <Col xs={4}>
                        <ColorPicker
                            controlId="button_primary"
                            label="Nuolipainikkeiden taustaväri"
                            value={colors?.button_primary || ''}
                            onChange={handleColorChange('button_primary')}
                            defaultValue={defaultColors.button_primary}
                            required={true}
                        />
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="button_secondary"
                            label="Nuolipainikkeiden korostettu taustaväri"
                            value={colors?.button_secondary || ''}
                            onChange={handleColorChange('button_secondary')}
                            defaultValue={defaultColors.button_secondary}
                            required={true}
                        />
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="button_color"
                            label="Nuolipainikkeiden tekstin väri"
                            value={colors?.button_color || ''}
                            onChange={handleColorChange('button_color')}
                            defaultValue={defaultColors.button_color}
                            required={true}
                        />
                    </Col>
                    <hr style={{margin: '0', border: 'none'}}></hr>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="primary_background"
                            label="Valinnan taustaväri"
                            value={colors?.primary_background || ''}
                            onChange={handleColorChange('primary_background')}
                            defaultValue={defaultColors.primary_background}
                            required={true}
                        />
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="primary_color"
                            label="Valinnan tekstin väri"
                            value={colors?.primary_color || ''}
                            onChange={handleColorChange('primary_color')}
                            defaultValue={defaultColors.primary_color}
                            required={true}
                        />
                    </Col>

                    <hr style={{margin: '0', border: 'none'}}></hr>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="correct_background"
                            label="Oikean vastauksen taustaväri"
                            value={colors?.correct_background || ''}
                            onChange={handleColorChange('correct_background')}
                            defaultValue={defaultColors.correct_background}
                            required={true}
                        />
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="correct_color"
                            label="Oikean vastauksen tekstin väri"
                            value={colors?.correct_color || ''}
                            onChange={handleColorChange('correct_color')}
                            defaultValue={defaultColors.correct_color}
                            required={true}
                        />
                    </Col>
                    <hr style={{margin: '0', border: 'none'}}></hr>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="incorrect_background"
                            label="Väärän vastauksen taustaväri"
                            value={colors?.incorrect_background || ''}
                            onChange={handleColorChange('incorrect_background')}
                            defaultValue={defaultColors.incorrect_background}
                            required={true}
                        />
                    </Col>
                    <Col xs={4}>
                        <ColorPicker
                            controlId="incorrect_color"
                            label="Väärän vastauksen tekstin väri"
                            value={colors?.incorrect_color || ''}
                            onChange={handleColorChange('incorrect_color')}
                            defaultValue={defaultColors.incorrect_color}
                            required={true}
                        />
                    </Col>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {theme_id && !game_id && !section_id && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={() => dispatch(closeModal())}>
                    {ADMIN_LANG.close}
                </Button>
                {showSave && (
                    <Button form='theme-modal-form' type='submit' variant='primary'>
                        {ADMIN_LANG.save}
                    </Button>
                )}
            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={delete_message}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />
        </Modal>
    );
}

export default ThemeModal;